import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { newsletterService } from '../../services/api';
import { FaTrash, FaSearch, FaPaperPlane, FaEnvelope, FaDownload } from 'react-icons/fa';
import Loading from '../../components/Common/Loading';

const Newsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailData, setEmailData] = useState({ subject: '', content: '' });

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const response = await newsletterService.getAll();
      
      console.log('Newsletter API Response:', response);
      
      let subscribersData = [];
      
      if (response && response.data) {
        if (response.data.success && Array.isArray(response.data.data)) {
          subscribersData = response.data.data;
        }
        else if (response.data.data && Array.isArray(response.data.data)) {
          subscribersData = response.data.data;
        }
        else if (Array.isArray(response.data)) {
          subscribersData = response.data;
        }
      }
      
      console.log('Extracted subscribers data:', subscribersData);
      setSubscribers(subscribersData);
      
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      setSubscribers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this subscriber?')) {
      try {
        await newsletterService.delete(id);
        const filteredSubscribers = subscribers.filter(s => s.id !== id);
        setSubscribers(filteredSubscribers);
      } catch (error) {
        console.error('Error deleting subscriber:', error);
      }
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!emailData.subject || !emailData.content) {
      alert('Please fill in the subject and content');
      return;
    }
    try {
      setSending(true);
      await newsletterService.send({
        subject: emailData.subject,
        content: emailData.content,
        recipients: subscribers.map(s => s.email)
      });
      alert(`Newsletter sent to ${subscribers.length} subscriber(s)`);
      setShowSendModal(false);
      setEmailData({ subject: '', content: '' });
    } catch (error) {
      console.error('Error sending newsletter:', error);
      alert('Error during sending');
    } finally {
      setSending(false);
    }
  };

  const getFilteredSubscribers = () => {
    if (!subscribers || !Array.isArray(subscribers) || subscribers.length === 0) {
      return [];
    }
    
    if (!searchTerm) {
      return subscribers;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return subscribers.filter(s => 
      s && s.email && s.email.toLowerCase().includes(searchLower)
    );
  };

  const filteredSubscribers = getFilteredSubscribers();
  
  const getNewSubscribersCount = () => {
    if (!subscribers || !Array.isArray(subscribers)) return 0;
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return subscribers.filter(s => {
      if (!s || !s.subscribed_at) return false;
      return new Date(s.subscribed_at) > sevenDaysAgo;
    }).length;
  };

  const newSubscribersCount = getNewSubscribersCount();

  const handleExport = () => {
    if (!filteredSubscribers || filteredSubscribers.length === 0) {
      alert('No subscribers to export');
      return;
    }
    
    const csv = [['Email', 'Subscription Date']];
    filteredSubscribers.forEach(s => {
      csv.push([s.email, s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString('en-US') : 'Unknown date']);
    });
    const blob = new Blob([csv.map(row => row.join(',')).join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <Loading />;
  }

  const subscribersCount = Array.isArray(subscribers) ? subscribers.length : 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Newsletter</h1>
          <p className="mt-1 text-gray-600">{subscribersCount} subscriber(s)</p>
        </div>
        <div className="flex gap-3">
          <button onClick={handleExport} className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <FaDownload />
            Export
          </button>
          <button onClick={() => setShowSendModal(true)} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2">
            <FaPaperPlane />
            Send Newsletter
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-3xl font-bold">{subscribersCount}</p>
              <p className="text-gray-600">Total Subscribers</p>
            </div>
            <FaEnvelope className="text-purple-600" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-3xl font-bold">{newSubscribersCount}</p>
              <p className="text-gray-600">New (7d)</p>
            </div>
            <FaEnvelope className="text-green-600" size={32} />
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:border-purple-500"
          />
        </div>
      </div>

      {/* Subscribers Table */}
      {filteredSubscribers.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
          <FaEnvelope className="mx-auto text-gray-300 mb-4" size={48} />
          <p>No subscribers found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscription Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSubscribers.map((subscriber) => (
                  <tr key={subscriber.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                          <FaEnvelope className="text-purple-600" size={12} />
                        </div>
                        {subscriber.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {subscriber.subscribed_at ? new Date(subscriber.subscribed_at).toLocaleString('en-US') : 'Unknown date'}
                    </td>
                    <td className="px-6 py-4">
                      <button onClick={() => handleDelete(subscriber.id)} className="text-red-600 hover:text-red-800" title="Delete">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Send Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Send Newsletter</h2>
              <button onClick={() => setShowSendModal(false)} className="text-gray-500">✕</button>
            </div>
            
            <form onSubmit={handleSend} className="space-y-4">
              <div>
                <label className="block text-gray-700 mb-2">Subject *</label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-purple-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2">Content *</label>
                <textarea
                  value={emailData.content}
                  onChange={(e) => setEmailData({...emailData, content: e.target.value})}
                  rows="10"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-purple-500 font-mono"
                  required
                  placeholder="Write your newsletter here..."
                />
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">📧 Sending to {subscribersCount} subscriber(s)</p>
              </div>
              
              <div className="flex justify-end gap-4 pt-4">
                <button type="button" onClick={() => setShowSendModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                <button type="submit" disabled={sending} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Newsletter;